<script>
import { crmPessoasAPI } from '../../../api/crmPessoas';
import { personContactsAPI } from '../../../api/personContacts';
import { specialtiesAPI } from '../../../api/specialties';
import { attendanceTypesAPI } from '../../../api/attendanceTypes';
import { useAlert } from 'dashboard/composables';
import { mapGetters } from 'vuex';

export default {
  name: 'PessoasView',
  data() {
    return {
      pessoas: [],
      loading: true,
      // Paginação no servidor: a conta da Alzi tem milhares de pessoas e trazer todas
      // de uma vez travava a tela. page é base 0, como no crm-ms.
      page: 0,
      pageSize: 50,
      pageSizeOptions: [25, 50, 100],
      totalElements: 0,
      totalPages: 0,
      showInactive: false,
      filters: {
        name: '',
        phone: '',
        personType: '',
        classification: '',
      },
      showModal: false,
      isEditing: false,
      isSaving: false,
      form: this.getEmptyForm(),
      showContactsModal: false,
      contactsPerson: null,
      personContacts: [],
      newContact: { name: '', phone: '', email: '' },
      specialties: [],
      attendanceTypes: [],
      // Diagnósticos aceitos no cadastro do paciente. Lista fixa porque é o vocabulário
      // clínico da casa, não algo que cada conta configura.
      diagnosisOptions: ['TEA', 'TDAH', 'TOD', 'Outros'],
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      currentUser: 'getCurrentUser',
      currentRole: 'getCurrentRole',
    }),
    isAdmin() {
      return this.currentRole === 'administrator';
    },
    nameLabel() {
      return this.form.personType === 'J'
        ? this.$t('PESSOAS.MODAL.COMPANY_NAME_LABEL')
        : this.$t('PESSOAS.MODAL.FULL_NAME_LABEL');
    },
    // Para Paciente, responsável/telefone/e-mail vivem em Contatos — um paciente tem
    // vários responsáveis, cada um com o seu número. Mostrar os campos na ficha faria o
    // usuário gravar na Pessoa um dado que a listagem e a agente leem do contato.
    // Profissional e Pessoa Jurídica continuam guardando esses dados na própria ficha.
    isPatientForm() {
      return this.form.classification === 'Paciente';
    },
    firstItemNumber() {
      return this.totalElements === 0 ? 0 : this.page * this.pageSize + 1;
    },
    lastItemNumber() {
      return this.page * this.pageSize + this.pessoas.length;
    },
    hasPreviousPage() {
      return this.page > 0;
    },
    hasNextPage() {
      return this.page + 1 < this.totalPages;
    },
  },
  async mounted() {
    await Promise.all([this.fetchSpecialties(), this.fetchAttendanceTypes()]);
    await this.fetchPessoas();
  },
  methods: {
    getEmptyForm() {
      return {
        id: null,
        name: '',
        personType: 'F',
        classification: '',
        documentNumber: '',
        contactName: '',
        email: '',
        phone: '',
        profession: '',
        birthDate: '',
        diagnosis: '',
        defaultAttendanceTypeId: '',
        specialtyIds: [],
        active: true,
      };
    },
    async fetchSpecialties() {
      try {
        const { data } = await specialtiesAPI.getAll(this.accountId, true);
        this.specialties = data || [];
      } catch {
        this.specialties = [];
      }
    },
    async fetchAttendanceTypes() {
      try {
        const { data } = await attendanceTypesAPI.getAll(this.accountId, true);
        this.attendanceTypes = data || [];
      } catch {
        this.attendanceTypes = [];
      }
    },
    // Especialidade só vale para profissional; diagnóstico e tipo de atendimento só
    // para paciente. Zerar o que não se aplica evita que uma troca de classificação
    // deixe resíduo do cadastro anterior gravado no banco.
    buildPayload() {
      const isProfessional = this.form.classification === 'Profissional';
      const isPatient = this.form.classification === 'Paciente';
      return {
        ...this.form,
        // Em paciente esses três pertencem ao Contato. Zerar aqui evita que trocar a
        // classificação de Profissional para Paciente deixe resíduo gravado na Pessoa.
        contactName: isPatient ? null : this.form.contactName,
        phone: isPatient ? null : this.form.phone,
        email: isPatient ? null : this.form.email,
        specialtyIds: isProfessional ? this.form.specialtyIds.map(Number) : [],
        diagnosis: isPatient ? this.form.diagnosis || null : null,
        defaultAttendanceTypeId:
          isPatient && this.form.defaultAttendanceTypeId
            ? Number(this.form.defaultAttendanceTypeId)
            : null,
      };
    },
    onPersonTypeChange() {
      // Profession/birth date only make sense for a physical person (patient);
      // clear any residual value so it isn't sent for a pessoa jurídica.
      if (this.form.personType === 'J') {
        this.form.profession = '';
        this.form.birthDate = '';
      }
    },
    // primaryPhone vem do contato principal; o phone da própria pessoa é resíduo dos
    // cadastros anteriores ao saneamento e serve só de fallback.
    formatPhone(phone) {
      if (!phone) return '-';
      return phone; // Simple return, can add mask logic here
    },
    async fetchPessoas() {
      this.loading = true;
      try {
        const { data } = await crmPessoasAPI.getPage(
          this.accountId,
          // "Mostrar inativos" desmarcado vira filtro no servidor: filtrar só na tela
          // deixaria páginas com menos linhas que o tamanho escolhido.
          { ...this.filters, activeOnly: this.showInactive ? '' : true },
          { page: this.page, size: this.pageSize }
        );
        this.pessoas = data.content || [];
        this.totalElements = data.totalElements || 0;
        this.totalPages = data.totalPages || 0;
        // Inativar o último item da última página deixaria a tela vazia.
        if (this.pessoas.length === 0 && this.page > 0 && this.totalPages > 0) {
          this.page = this.totalPages - 1;
          await this.fetchPessoas();
        }
      } catch {
        useAlert('Erro ao buscar Pessoas do CRM.');
      } finally {
        this.loading = false;
      }
    },
    // Filtro novo sempre volta para a primeira página: a página atual pode nem existir
    // no resultado filtrado.
    applyFilters() {
      this.page = 0;
      this.fetchPessoas();
    },
    clearFilters() {
      this.filters = {
        name: '',
        phone: '',
        personType: '',
        classification: '',
      };
      this.applyFilters();
    },
    goToPage(page) {
      if (page < 0 || (this.totalPages && page >= this.totalPages)) return;
      this.page = page;
      this.fetchPessoas();
    },
    openAddModal() {
      this.form = this.getEmptyForm();
      this.isEditing = false;
      this.showModal = true;
    },
    openEditModal(pessoa) {
      this.form = { ...this.getEmptyForm(), ...pessoa };
      // A API devolve null quando nunca foi preenchido; os <select>/checkbox precisam
      // de '' e [] para não renderizarem vazios de um jeito que o v-model não reverte.
      this.form.diagnosis = pessoa.diagnosis || '';
      this.form.defaultAttendanceTypeId = pessoa.defaultAttendanceTypeId || '';
      this.form.specialtyIds = [...(pessoa.specialtyIds || [])];
      this.isEditing = true;
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.form = this.getEmptyForm();
    },
    async savePessoa() {
      if (!this.form.name || !this.form.personType) {
        useAlert('Preencha TODOS os campos obrigatórios (*).');
        return;
      }

      this.isSaving = true;
      const payload = this.buildPayload();
      try {
        if (this.isEditing) {
          await crmPessoasAPI.update(this.form.id, payload, this.accountId);
          useAlert('Pessoa atualizada com sucesso!');
        } else {
          await crmPessoasAPI.create(payload, this.accountId);
          useAlert('Pessoa cadastrada com sucesso!');
        }
        this.closeModal();
        await this.fetchPessoas();
      } catch {
        useAlert('Erro ao salvar Pessoa no CRM.');
      } finally {
        this.isSaving = false;
      }
    },
    isPaciente(pessoa) {
      return (pessoa.classification || '').toLowerCase() === 'paciente';
    },
    openRegistros(pessoa) {
      this.$router.push({
        name: 'crm_registros',
        query: { personId: pessoa.id, personName: pessoa.name },
      });
    },
    async toggleStatus(pessoa) {
      try {
        await crmPessoasAPI.updateStatus(
          pessoa.id,
          !pessoa.active,
          this.currentUser?.name,
          this.accountId
        );
        await this.fetchPessoas();
      } catch {
        useAlert(this.$t('PESSOAS.ERRORS.STATUS'));
      }
    },
    async openContactsModal(pessoa) {
      this.contactsPerson = pessoa;
      this.newContact = { name: '', phone: '', email: '' };
      this.showContactsModal = true;
      await this.fetchPersonContacts();
    },
    closeContactsModal() {
      this.showContactsModal = false;
      this.contactsPerson = null;
      this.personContacts = [];
    },
    async fetchPersonContacts() {
      try {
        const { data } = await personContactsAPI.getByPerson(
          this.accountId,
          this.contactsPerson.id
        );
        this.personContacts = data || [];
      } catch {
        this.personContacts = [];
        useAlert(this.$t('PESSOAS.ERRORS.CONTACTS_FETCH'));
      }
    },
    async addContact() {
      if (!this.newContact.name.trim() && !this.newContact.phone.trim()) {
        useAlert(this.$t('PESSOAS.CONTACTS_MODAL.CONTACT_REQUIRED'));
        return;
      }
      try {
        await personContactsAPI.create(this.accountId, {
          personId: this.contactsPerson.id,
          name: this.newContact.name.trim(),
          phone: this.newContact.phone.trim(),
          email: this.newContact.email.trim(),
          isPrimary: false,
        });
        this.newContact = { name: '', phone: '', email: '' };
        await this.fetchPersonContacts();
      } catch {
        useAlert(this.$t('PESSOAS.ERRORS.CONTACTS_SAVE'));
      }
    },
    async removeContact(contactId) {
      try {
        await personContactsAPI.delete(this.accountId, contactId);
        await this.fetchPersonContacts();
      } catch {
        useAlert(this.$t('PESSOAS.ERRORS.CONTACTS_DELETE'));
      }
    },
  },
};
</script>

<template>
  <!-- min-w-0: sem ele a tela cresce até a largura da tabela e a barra
       de rolagem horizontal nunca aparece (os botões somem à direita). -->
  <div
    class="flex-1 min-w-0 px-6 py-6 w-full h-full bg-slate-50 overflow-y-auto"
  >
    <div class="flex items-center justify-between pb-6">
      <h1 class="text-2xl font-bold text-slate-800">
        {{ $t('PESSOAS.TITLE') }}
      </h1>
      <button
        class="bg-fluxi-primary hover:bg-fluxi-primary/90 text-white font-medium py-2 px-4 rounded-md transition-colors shadow-sm"
        @click="openAddModal"
      >
        {{ $t('PESSOAS.ADD_BUTTON') }}
      </button>
    </div>

    <!-- Filtros -->
    <div
      class="bg-white rounded-lg shadow-sm border border-slate-200 p-5 mb-6 flex flex-wrap items-end gap-4"
    >
      <div class="flex flex-col">
        <label class="text-xs font-semibold text-slate-600 mb-1">{{
          $t('PESSOAS.FILTERS.NAME_LABEL')
        }}</label>
        <input
          v-model="filters.name"
          type="text"
          :placeholder="$t('PESSOAS.FILTERS.NAME_PLACEHOLDER')"
          class="border border-slate-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none w-48"
          @keyup.enter="applyFilters"
        />
      </div>
      <div class="flex flex-col">
        <label class="text-xs font-semibold text-slate-600 mb-1">{{
          $t('PESSOAS.FILTERS.PHONE_LABEL')
        }}</label>
        <input
          v-model="filters.phone"
          type="text"
          :placeholder="$t('PESSOAS.FILTERS.PHONE_PLACEHOLDER')"
          class="border border-slate-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none w-48"
          @keyup.enter="applyFilters"
        />
      </div>
      <div class="flex flex-col">
        <label class="text-xs font-semibold text-slate-600 mb-1">{{
          $t('PESSOAS.FILTERS.NATURE_LABEL')
        }}</label>
        <select
          v-model="filters.personType"
          class="border border-slate-300 rounded bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none w-40"
        >
          <option value="">{{ $t('PESSOAS.FILTERS.ALL_NATURES') }}</option>
          <option value="F">{{ $t('PESSOAS.FILTERS.PHYSICAL') }}</option>
          <option value="J">{{ $t('PESSOAS.FILTERS.LEGAL') }}</option>
        </select>
      </div>
      <div class="flex flex-col">
        <label class="text-xs font-semibold text-slate-600 mb-1">{{
          $t('PESSOAS.FILTERS.TYPE_LABEL')
        }}</label>
        <select
          v-model="filters.classification"
          class="border border-slate-300 rounded bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none w-40"
        >
          <option value="">{{ $t('PESSOAS.FILTERS.ALL_TYPES') }}</option>
          <option value="Paciente">
            {{ $t('PESSOAS.FILTERS.PATIENT') }}
          </option>
          <option value="Profissional">
            {{ $t('PESSOAS.FILTERS.PROFESSIONAL') }}
          </option>
        </select>
      </div>
      <label class="flex items-center gap-2 text-sm font-medium text-slate-600">
        <input v-model="showInactive" type="checkbox" @change="applyFilters" />
        {{ $t('PESSOAS.FILTERS.SHOW_INACTIVE') }}
      </label>
      <div class="flex gap-2 ml-auto">
        <button
          class="bg-fluxi-primary hover:bg-fluxi-primary/90 text-white font-medium py-2 px-6 rounded text-sm transition-colors shadow-sm"
          @click="applyFilters"
        >
          {{ $t('PESSOAS.FILTERS.FILTER_BUTTON') }}
        </button>
        <button
          class="bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium py-2 px-6 rounded text-sm transition-colors shadow-sm border border-slate-200"
          @click="clearFilters"
        >
          {{ $t('PESSOAS.FILTERS.CLEAR_BUTTON') }}
        </button>
      </div>
    </div>

    <!-- Tabela -->
    <div
      class="bg-white rounded-lg shadow-sm border border-slate-200 overflow-x-auto"
    >
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-slate-50">
          <tr>
            <th
              class="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap hidden lg:table-cell"
            >
              {{ $t('PESSOAS.TABLE.ID') }}
            </th>
            <th
              class="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap"
            >
              {{ $t('PESSOAS.TABLE.NAME') }}
            </th>
            <th
              class="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap"
            >
              {{ $t('PESSOAS.TABLE.CONTACT_NAME') }}
            </th>
            <th
              class="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap"
            >
              {{ $t('PESSOAS.TABLE.PHONE') }}
            </th>
            <th
              class="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap hidden xl:table-cell"
            >
              {{ $t('PESSOAS.TABLE.NATURE') }}
            </th>
            <th
              class="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap"
            >
              {{ $t('PESSOAS.TABLE.TYPES') }}
            </th>
            <th
              class="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap"
            >
              {{ $t('PESSOAS.TABLE.STATUS') }}
            </th>
            <!-- Ações fixas à direita: continuam visíveis com a tabela rolada. -->
            <th
              class="sticky right-0 px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50 shadow-[-8px_0_8px_-8px_rgba(15,23,42,0.15)]"
            >
              {{ $t('PESSOAS.TABLE.ACTIONS') }}
            </th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-100">
          <tr v-if="loading">
            <td
              colspan="8"
              class="px-4 py-8 text-center text-sm text-slate-500"
            >
              {{ $t('PESSOAS.TABLE.LOADING') }}
            </td>
          </tr>
          <tr v-else-if="pessoas.length === 0">
            <td
              colspan="8"
              class="px-4 py-8 text-center text-sm text-slate-500"
            >
              {{ $t('PESSOAS.TABLE.EMPTY') }}
            </td>
          </tr>
          <template v-else>
            <tr
              v-for="pessoa in pessoas"
              :key="pessoa.id"
              class="group hover:bg-slate-50 transition-colors"
            >
              <td
                class="hidden px-4 py-3 text-sm whitespace-nowrap lg:table-cell text-slate-500"
              >
                {{ pessoa.id }}
              </td>
              <td
                class="px-4 py-3 text-sm font-medium uppercase text-slate-700 max-w-[16rem] truncate"
                :title="pessoa.name"
              >
                {{ pessoa.name }}
              </td>
              <td
                class="px-4 py-3 text-sm uppercase text-slate-600 max-w-[12rem] truncate"
                :title="pessoa.primaryContactName || pessoa.contactName || ''"
              >
                {{ pessoa.primaryContactName || pessoa.contactName || '-' }}
              </td>
              <td
                class="px-4 py-3 font-mono text-sm whitespace-nowrap text-slate-600"
              >
                {{ formatPhone(pessoa.primaryPhone || pessoa.phone) }}
              </td>
              <td
                class="hidden px-4 py-3 text-sm whitespace-nowrap xl:table-cell text-slate-600"
              >
                {{
                  pessoa.personType === 'J'
                    ? $t('PESSOAS.TABLE.COMPANY')
                    : $t('PESSOAS.TABLE.INDIVIDUAL')
                }}
              </td>
              <td class="px-4 py-3 whitespace-nowrap">
                <span
                  v-if="pessoa.classification"
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                >
                  {{ pessoa.classification }}
                </span>
                <span v-else class="text-xs text-slate-400">-</span>
              </td>
              <td class="px-4 py-3 whitespace-nowrap">
                <span
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                  :class="
                    pessoa.active === false
                      ? 'bg-slate-100 text-slate-500'
                      : 'bg-emerald-50 text-emerald-700'
                  "
                >
                  {{
                    pessoa.active === false
                      ? $t('PESSOAS.TABLE.INACTIVE')
                      : $t('PESSOAS.TABLE.ACTIVE')
                  }}
                </span>
              </td>
              <td
                class="sticky right-0 px-3 py-2 bg-white group-hover:bg-slate-50 shadow-[-8px_0_8px_-8px_rgba(15,23,42,0.15)]"
              >
                <div class="flex items-center justify-center gap-1">
                  <button
                    v-tooltip.top="$t('PESSOAS.TABLE.EDIT')"
                    :aria-label="$t('PESSOAS.TABLE.EDIT')"
                    class="flex items-center justify-center rounded-md size-8 text-fluxi-primary hover:bg-fluxi-primary/10"
                    @click="openEditModal(pessoa)"
                  >
                    <span class="i-lucide-pencil size-4" />
                  </button>
                  <button
                    v-tooltip.top="$t('PESSOAS.TABLE.CONTACTS')"
                    :aria-label="$t('PESSOAS.TABLE.CONTACTS')"
                    class="flex items-center justify-center rounded-md size-8 text-slate-600 hover:bg-slate-100"
                    @click="openContactsModal(pessoa)"
                  >
                    <span class="i-lucide-contact-round size-4" />
                  </button>
                  <button
                    v-if="isPaciente(pessoa)"
                    v-tooltip.top="$t('PESSOAS.TABLE.DAILY_RECORDS')"
                    :aria-label="$t('PESSOAS.TABLE.DAILY_RECORDS')"
                    class="flex items-center justify-center text-green-700 rounded-md size-8 hover:bg-green-50"
                    @click="openRegistros(pessoa)"
                  >
                    <span class="i-lucide-clipboard-list size-4" />
                  </button>
                  <button
                    v-if="isAdmin"
                    v-tooltip.top="
                      pessoa.active === false
                        ? $t('PESSOAS.TABLE.ACTIVATE')
                        : $t('PESSOAS.TABLE.DEACTIVATE')
                    "
                    :aria-label="
                      pessoa.active === false
                        ? $t('PESSOAS.TABLE.ACTIVATE')
                        : $t('PESSOAS.TABLE.DEACTIVATE')
                    "
                    class="flex items-center justify-center rounded-md size-8 text-amber-700 hover:bg-amber-50"
                    @click="toggleStatus(pessoa)"
                  >
                    <span
                      class="size-4"
                      :class="
                        pessoa.active === false
                          ? 'i-lucide-user-check'
                          : 'i-lucide-user-x'
                      "
                    />
                  </button>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <!-- Paginação -->
    <div
      v-if="totalElements > 0"
      class="flex flex-wrap items-center justify-between gap-3 pt-4 text-sm text-slate-600"
    >
      <span>
        {{
          $t('PESSOAS.PAGINATION.SHOWING', {
            from: firstItemNumber,
            to: lastItemNumber,
            total: totalElements,
          })
        }}
      </span>
      <div class="flex items-center gap-2">
        <label class="flex items-center gap-2">
          {{ $t('PESSOAS.PAGINATION.PER_PAGE') }}
          <select
            v-model.number="pageSize"
            class="border border-slate-300 rounded bg-white px-2 py-1 text-sm outline-none"
            @change="applyFilters"
          >
            <option v-for="size in pageSizeOptions" :key="size" :value="size">
              {{ size }}
            </option>
          </select>
        </label>
        <button
          :disabled="!hasPreviousPage || loading"
          :aria-label="$t('PESSOAS.PAGINATION.FIRST')"
          class="flex items-center justify-center rounded-md size-8 border border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
          @click="goToPage(0)"
        >
          <span class="i-lucide-chevrons-left size-4" />
        </button>
        <button
          :disabled="!hasPreviousPage || loading"
          :aria-label="$t('PESSOAS.PAGINATION.PREVIOUS')"
          class="flex items-center justify-center rounded-md size-8 border border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
          @click="goToPage(page - 1)"
        >
          <span class="i-lucide-chevron-left size-4" />
        </button>
        <span class="whitespace-nowrap">
          {{
            $t('PESSOAS.PAGINATION.PAGE_OF', {
              page: page + 1,
              total: totalPages,
            })
          }}
        </span>
        <button
          :disabled="!hasNextPage || loading"
          :aria-label="$t('PESSOAS.PAGINATION.NEXT')"
          class="flex items-center justify-center rounded-md size-8 border border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
          @click="goToPage(page + 1)"
        >
          <span class="i-lucide-chevron-right size-4" />
        </button>
        <button
          :disabled="!hasNextPage || loading"
          :aria-label="$t('PESSOAS.PAGINATION.LAST')"
          class="flex items-center justify-center rounded-md size-8 border border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
          @click="goToPage(totalPages - 1)"
        >
          <span class="i-lucide-chevrons-right size-4" />
        </button>
      </div>
    </div>

    <!-- Modal Adicionar/Editar -->
    <div
      v-if="showModal"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
    >
      <div
        class="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col"
      >
        <div
          class="px-6 py-4 border-b border-slate-200 flex justify-between items-center"
        >
          <h2 class="text-lg font-bold text-slate-800">
            {{
              isEditing
                ? $t('PESSOAS.MODAL.EDIT_TITLE')
                : $t('PESSOAS.MODAL.ADD_TITLE')
            }}
          </h2>
          <button
            class="text-slate-400 hover:text-slate-600 text-xl font-bold"
            @click="closeModal"
          >
            <span class="i-lucide-x size-5" />
          </button>
        </div>

        <div class="px-6 py-4 overflow-y-auto flex-grow">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="col-span-2">
              <label class="block text-sm font-semibold text-slate-700 mb-1">{{
                nameLabel
              }}</label>
              <input
                v-model="form.name"
                type="text"
                class="w-full border border-slate-300 rounded px-3 py-2 focus:ring-2 focus:ring-fluxi-primary focus:border-fluxi-primary outline-none"
                required
              />
            </div>

            <div>
              <label class="block text-sm font-semibold text-slate-700 mb-1">{{
                $t('PESSOAS.MODAL.NATURE_LABEL')
              }}</label>
              <select
                v-model="form.personType"
                class="w-full border border-slate-300 rounded bg-white px-3 py-2 focus:ring-2 focus:ring-fluxi-primary focus:border-fluxi-primary outline-none"
                required
                @change="onPersonTypeChange"
              >
                <option value="F">{{ $t('PESSOAS.FILTERS.PHYSICAL') }}</option>
                <option value="J">{{ $t('PESSOAS.FILTERS.LEGAL') }}</option>
              </select>
            </div>

            <div>
              <label class="block text-sm font-semibold text-slate-700 mb-1">{{
                $t('PESSOAS.MODAL.CLASSIFICATION_LABEL')
              }}</label>
              <select
                v-model="form.classification"
                class="w-full border border-slate-300 rounded bg-white px-3 py-2 focus:ring-2 focus:ring-fluxi-primary focus:border-fluxi-primary outline-none"
              >
                <option value="">{{ $t('PESSOAS.MODAL.NONE') }}</option>
                <option value="Paciente">
                  {{ $t('PESSOAS.MODAL.PATIENT') }}
                </option>
                <option value="Profissional">
                  {{ $t('PESSOAS.MODAL.PROFESSIONAL') }}
                </option>
              </select>
            </div>

            <div>
              <label class="block text-sm font-semibold text-slate-700 mb-1">{{
                $t('PESSOAS.MODAL.DOCUMENT_LABEL')
              }}</label>
              <input
                v-model="form.documentNumber"
                type="text"
                class="w-full border border-slate-300 rounded px-3 py-2 focus:ring-2 focus:ring-fluxi-primary focus:border-fluxi-primary outline-none"
              />
            </div>

            <div v-if="!isPatientForm">
              <label class="block text-sm font-semibold text-slate-700 mb-1">{{
                $t('PESSOAS.MODAL.CONTACT_NAME_LABEL')
              }}</label>
              <input
                v-model="form.contactName"
                type="text"
                class="w-full border border-slate-300 rounded px-3 py-2 focus:ring-2 focus:ring-fluxi-primary focus:border-fluxi-primary outline-none"
              />
            </div>

            <div v-if="!isPatientForm">
              <label class="block text-sm font-semibold text-slate-700 mb-1">{{
                $t('PESSOAS.MODAL.EMAIL_LABEL')
              }}</label>
              <input
                v-model="form.email"
                type="email"
                class="w-full border border-slate-300 rounded px-3 py-2 focus:ring-2 focus:ring-fluxi-primary focus:border-fluxi-primary outline-none"
              />
            </div>

            <div v-if="!isPatientForm">
              <label class="block text-sm font-semibold text-slate-700 mb-1">{{
                $t('PESSOAS.MODAL.PHONE_LABEL')
              }}</label>
              <input
                v-model="form.phone"
                type="text"
                class="w-full border border-slate-300 rounded px-3 py-2 focus:ring-2 focus:ring-fluxi-primary focus:border-fluxi-primary outline-none"
              />
            </div>

            <div v-else class="col-span-1 md:col-span-2">
              <p
                class="px-4 py-3 text-sm border rounded bg-slate-50 border-slate-200 text-slate-600"
              >
                {{ $t('PESSOAS.MODAL.CONTACTS_HINT') }}
              </p>
            </div>

            <div v-if="form.personType === 'F'">
              <label class="block text-sm font-semibold text-slate-700 mb-1">{{
                $t('PESSOAS.MODAL.PROFESSION_LABEL')
              }}</label>
              <input
                v-model="form.profession"
                type="text"
                class="w-full border border-slate-300 rounded px-3 py-2 focus:ring-2 focus:ring-fluxi-primary focus:border-fluxi-primary outline-none"
              />
            </div>

            <div v-if="form.personType === 'F'">
              <label class="block text-sm font-semibold text-slate-700 mb-1">{{
                $t('PESSOAS.MODAL.BIRTH_DATE_LABEL')
              }}</label>
              <input
                v-model="form.birthDate"
                type="date"
                class="w-full border border-slate-300 rounded px-3 py-2 focus:ring-2 focus:ring-fluxi-primary focus:border-fluxi-primary outline-none"
              />
            </div>

            <div v-if="form.classification === 'Paciente'">
              <label class="block text-sm font-semibold text-slate-700 mb-1">{{
                $t('PESSOAS.MODAL.DIAGNOSIS_LABEL')
              }}</label>
              <select
                v-model="form.diagnosis"
                class="w-full border border-slate-300 rounded bg-white px-3 py-2 focus:ring-2 focus:ring-fluxi-primary focus:border-fluxi-primary outline-none"
              >
                <option value="">{{ $t('PESSOAS.MODAL.NONE') }}</option>
                <option
                  v-for="option in diagnosisOptions"
                  :key="option"
                  :value="option"
                >
                  {{ option }}
                </option>
              </select>
            </div>

            <div v-if="form.classification === 'Paciente'">
              <label class="block text-sm font-semibold text-slate-700 mb-1">{{
                $t('PESSOAS.MODAL.ATTENDANCE_TYPE_LABEL')
              }}</label>
              <select
                v-model="form.defaultAttendanceTypeId"
                class="w-full border border-slate-300 rounded bg-white px-3 py-2 focus:ring-2 focus:ring-fluxi-primary focus:border-fluxi-primary outline-none"
              >
                <option value="">{{ $t('PESSOAS.MODAL.NONE') }}</option>
                <option
                  v-for="type in attendanceTypes"
                  :key="type.id"
                  :value="type.id"
                >
                  {{ type.description }}
                </option>
              </select>
            </div>

            <div
              v-if="form.classification === 'Profissional'"
              class="col-span-1 md:col-span-2"
            >
              <label class="block text-sm font-semibold text-slate-700 mb-1">{{
                $t('PESSOAS.MODAL.SPECIALTIES_LABEL')
              }}</label>
              <p v-if="specialties.length === 0" class="text-sm text-slate-500">
                {{ $t('PESSOAS.MODAL.SPECIALTIES_EMPTY') }}
              </p>
              <div
                v-else
                class="flex flex-wrap gap-x-6 gap-y-2 p-3 border rounded border-slate-300"
              >
                <label
                  v-for="specialty in specialties"
                  :key="specialty.id"
                  class="flex items-center gap-2 text-sm text-slate-700"
                >
                  <input
                    v-model="form.specialtyIds"
                    type="checkbox"
                    :value="specialty.id"
                  />
                  {{ specialty.name }}
                </label>
              </div>
              <p class="mt-1 text-xs text-slate-500">
                {{ $t('PESSOAS.MODAL.SPECIALTIES_HINT') }}
              </p>
            </div>
          </div>
        </div>

        <div
          class="px-6 py-4 border-t border-slate-200 flex justify-end gap-3 bg-slate-50"
        >
          <button
            class="px-4 py-2 font-medium text-slate-600 bg-white border border-slate-300 rounded hover:bg-slate-50 transition-colors"
            @click="closeModal"
          >
            {{ $t('PESSOAS.MODAL.CANCEL') }}
          </button>
          <button
            :disabled="isSaving"
            class="px-6 py-2 font-medium text-white bg-fluxi-primary rounded hover:bg-fluxi-primary/90 transition-colors disabled:opacity-50"
            @click="savePessoa"
          >
            {{
              isSaving ? $t('PESSOAS.MODAL.SAVING') : $t('PESSOAS.MODAL.SAVE')
            }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal Contatos -->
    <div
      v-if="showContactsModal"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
    >
      <div
        class="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col"
      >
        <div
          class="px-6 py-4 border-b border-slate-200 flex justify-between items-center"
        >
          <h2 class="text-lg font-bold text-slate-800">
            {{ $t('PESSOAS.CONTACTS_MODAL.TITLE') }}
            <span class="text-slate-500 font-normal">
              {{ `— ${contactsPerson?.name || ''}` }}
            </span>
          </h2>
          <button
            class="text-slate-400 hover:text-slate-600 text-xl font-bold"
            @click="closeContactsModal"
          >
            <span class="i-lucide-x size-5" />
          </button>
        </div>

        <div class="px-6 py-4 overflow-y-auto flex-grow space-y-3">
          <div
            v-for="contact in personContacts"
            :key="contact.id"
            class="flex items-center justify-between px-3 py-2 border border-slate-200 rounded"
          >
            <div class="text-sm">
              <span class="font-medium text-slate-700">{{
                contact.name || '-'
              }}</span>
              <span class="text-slate-500">
                {{ ` · ${contact.phone || '-'}` }}
              </span>
              <span v-if="contact.email" class="text-slate-500">
                {{ ` · ${contact.email}` }}
              </span>
              <span
                v-if="contact.isPrimary"
                class="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
              >
                {{ $t('PESSOAS.CONTACTS_MODAL.PRIMARY') }}
              </span>
            </div>
            <button
              v-if="!contact.isPrimary"
              class="text-xs font-medium text-red-600 hover:text-red-700"
              @click="removeContact(contact.id)"
            >
              {{ $t('PESSOAS.CONTACTS_MODAL.REMOVE') }}
            </button>
          </div>
          <p v-if="personContacts.length === 0" class="text-sm text-slate-500">
            {{ $t('PESSOAS.CONTACTS_MODAL.EMPTY') }}
          </p>

          <div class="pt-3 mt-3 border-t border-slate-200">
            <p class="text-sm font-semibold text-slate-700 mb-2">
              {{ $t('PESSOAS.CONTACTS_MODAL.ADD_TITLE') }}
            </p>
            <div class="grid grid-cols-3 gap-2">
              <input
                v-model="newContact.name"
                type="text"
                :placeholder="$t('PESSOAS.CONTACTS_MODAL.NAME_PLACEHOLDER')"
                class="border border-slate-300 rounded px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-fluxi-primary"
              />
              <input
                v-model="newContact.phone"
                type="text"
                :placeholder="$t('PESSOAS.CONTACTS_MODAL.PHONE_PLACEHOLDER')"
                class="border border-slate-300 rounded px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-fluxi-primary"
              />
              <input
                v-model="newContact.email"
                type="email"
                :placeholder="$t('PESSOAS.CONTACTS_MODAL.EMAIL_PLACEHOLDER')"
                class="border border-slate-300 rounded px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-fluxi-primary"
              />
            </div>
            <button
              class="mt-2 px-4 py-1.5 text-sm font-medium text-white rounded bg-fluxi-primary hover:bg-fluxi-primary/90"
              @click="addContact"
            >
              {{ $t('PESSOAS.CONTACTS_MODAL.ADD_BUTTON') }}
            </button>
          </div>
        </div>

        <div
          class="px-6 py-4 border-t border-slate-200 flex justify-end bg-slate-50"
        >
          <button
            class="px-4 py-2 font-medium text-slate-600 bg-white border border-slate-300 rounded hover:bg-slate-50 transition-colors"
            @click="closeContactsModal"
          >
            {{ $t('PESSOAS.MODAL.CANCEL') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
